import { AgenticScoreChangeEvent } from "./AgenticScoreChangeEvent";
import { CommentEvent } from "./CommentEvent";
import { JobChangeEvent } from "./JobChangeEvent";
import { OwnerChangeEvent } from "./OwnerChangeEvent";
import { PermanentStatusChangeEvent } from "./PermanentStatusChangeEvent";
import { StatusChangeEvent } from "./StatusChangeEvent";
import { SummaryChangeEvent } from "./SummaryChangeEvent";

import { Column, ErrorText, LoadingIndicator, Text } from "@/components";
import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useGetCandidacyAgenticScoreChanges } from "@/hooks/queries/useGetCandidacyAgenticScoreChanges";
import { useGetCandidacyJobChanges } from "@/hooks/queries/useGetCandidacyJobChanges";
import { useGetCandidacyOwnerChanges } from "@/hooks/queries/useGetCandidacyOwnerChanges";
import { useGetCandidacyStatusChanges } from "@/hooks/queries/useGetCandidacyStatusChanges";
import { useGetCandidacySummaryChanges } from "@/hooks/queries/useGetCandidacySummaryChanges";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetInternalCandidacyComments } from "@/hooks/queries/useGetInternalCandidacyComments";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { misc } from "@/lib/misc";
import { novaClient } from "@/lib/trpc";
import type { Activity } from "@/types";

export const InternalActivity = () => {
  /* Context */
  const { candidacyId } = useSheetScope();
  const { jobId, workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });
  const agenticScoreChangesQuery = useGetCandidacyAgenticScoreChanges({
    candidacyId,
  });
  const commentsQuery = useGetInternalCandidacyComments({ candidacyId });
  const jobChangesQuery = useGetCandidacyJobChanges({ candidacyId });
  const ownerChangesQuery = useGetCandidacyOwnerChanges({ candidacyId });
  const statusChangesQuery = useGetCandidacyStatusChanges({ candidacyId });
  const summaryChangesQuery = useGetCandidacySummaryChanges({ candidacyId });

  const permanentStatusChangesQuery =
    novaClient.internal.user.status.active.permanent.listByUserId.useQuery({
      userId: workerId,
    });

  /* React Hooks */
  const currentUser = useGetCurrentUser();

  /* Helpers */
  const guacQueries = [
    agenticScoreChangesQuery,
    commentsQuery,
    jobChangesQuery,
    ownerChangesQuery,
    statusChangesQuery,
    summaryChangesQuery,
  ];
  const candidateFullName = misc.toTitleCase(
    `${userApi.data?.profile.firstName ?? ""} ${userApi.data?.profile.lastName ?? ""}`.trim(),
  );

  const buildFeed = (): Activity[] => {
    const feed: Activity[] = [
      ...(agenticScoreChangesQuery.data ?? []).map(
        (candidacyAgenticScoreChange) => ({
          type: "candidacyAgenticScoreChange" as const,
          candidacyAgenticScoreChange,
          createdAt: candidacyAgenticScoreChange.createdAt,
        }),
      ),
      ...(commentsQuery.data ?? []).map((internalCandidacyComment) => ({
        type: "internalCandidacyComment" as const,
        internalCandidacyComment,
        createdAt: internalCandidacyComment.createdAt,
      })),
      ...(jobChangesQuery.data ?? []).map((candidacyJobChange) => ({
        type: "candidacyJobChange" as const,
        candidacyJobChange,
        createdAt: candidacyJobChange.createdAt,
      })),
      ...(ownerChangesQuery.data ?? []).map((candidacyOwnerChange) => ({
        type: "candidacyOwnerChange" as const,
        candidacyOwnerChange,
        createdAt: candidacyOwnerChange.createdAt,
      })),
      ...(statusChangesQuery.data ?? []).map((candidacyStatusChange) => ({
        type: "candidacyStatusChange" as const,
        candidacyStatusChange,
        createdAt: candidacyStatusChange.createdAt,
      })),
      ...(summaryChangesQuery.data ?? []).map((candidacySummaryChange) => ({
        type: "candidacySummaryChange" as const,
        candidacySummaryChange,
        createdAt: candidacySummaryChange.createdAt,
      })),
      ...(permanentStatusChangesQuery.data ?? []).map(
        (userPermanentStatusChange) => ({
          type: "userPermanentStatusChange" as const,
          userPermanentStatusChange,
          createdAt: userPermanentStatusChange.createdAt,
        }),
      ),
    ];

    return feed.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  };

  /* Render */
  const renderEvent = (activity: Activity, currentUserId: number) => {
    switch (activity.type) {
      case "candidacyAgenticScoreChange":
        return (
          <AgenticScoreChangeEvent
            currentUserId={currentUserId}
            key={`score-${String(activity.candidacyAgenticScoreChange.id)}`}
            scoreChange={activity.candidacyAgenticScoreChange}
          />
        );
      case "candidacyJobChange":
        return (
          <JobChangeEvent
            candidateFullName={candidateFullName}
            currentUserId={currentUserId}
            jobChange={activity.candidacyJobChange}
            key={`job-${String(activity.candidacyJobChange.id)}`}
          />
        );
      case "candidacyOwnerChange":
        return (
          <OwnerChangeEvent
            candidateFullName={candidateFullName}
            currentUserId={currentUserId}
            key={`owner-${String(activity.candidacyOwnerChange.id)}`}
            ownerChange={activity.candidacyOwnerChange}
          />
        );
      case "candidacyStatusChange":
        return (
          <StatusChangeEvent
            candidateFullName={candidateFullName}
            currentUserId={currentUserId}
            key={`status-${String(activity.candidacyStatusChange.id)}`}
            statusChange={activity.candidacyStatusChange}
          />
        );
      case "candidacySummaryChange":
        return (
          <SummaryChangeEvent
            candidateFullName={candidateFullName}
            currentUserId={currentUserId}
            key={`summary-${String(activity.candidacySummaryChange.id)}`}
            summaryChange={activity.candidacySummaryChange}
          />
        );
      case "internalCandidacyComment":
        return (
          <CommentEvent
            candidateFullName={candidateFullName}
            comment={activity.internalCandidacyComment}
            currentUserId={currentUserId}
            key={`comment-${String(activity.internalCandidacyComment.id)}`}
          />
        );
      case "userPermanentStatusChange":
        return (
          <PermanentStatusChangeEvent
            candidateFullName={candidateFullName}
            currentUserId={currentUserId}
            key={`permanent-${String(activity.userPermanentStatusChange.id)}`}
            permanentStatusChange={activity.userPermanentStatusChange}
          />
        );
    }
  };

  const render = () => {
    if (
      guacQueries.some((query) => query.isPending) ||
      permanentStatusChangesQuery.isLoading ||
      !currentUser
    ) {
      return <LoadingIndicator />;
    }

    if (
      guacQueries.some((query) => query.isError) ||
      permanentStatusChangesQuery.isError
    ) {
      return <ErrorText errorMessage="Couldn't load activity. Try again." />;
    }

    const feed = buildFeed();

    if (feed.length === 0) {
      return (
        <Text className="text-sm font-normal text-slate-500">
          No activity yet.
        </Text>
      );
    }

    return (
      <Column className="gap-3 pt-2">
        {feed.map((activity) => renderEvent(activity, currentUser.id))}
      </Column>
    );
  };

  return render();
};
